import { keepPreviousData, useQuery } from '@tanstack/react-query';
import qs from 'query-string';

import type { AdvancedClosedOrderStatus } from '@/constants/advancedOrder';

import { useLimitOrders } from '@/hooks/useLimitOrders';

import { getTradeIdentity } from '@/utils/0x/history/status';
import type { MatchaTradeHistoryEntry, TradeHistoryResponse } from '@/utils/0x/history/types';
import { fetcher } from '@/utils/fetcher';
import type { ChainAddress, EthereumAddress, SolanaAddress } from '@/utils/models';
import { routes } from '@/utils/routes';

export type LimitOrderFunders = {
  ethereum: EthereumAddress | undefined;
  solana: SolanaAddress | undefined;
};

export function useWalletOpenLimitOrders({
  funders,
  searchTerm,
  chainId,
  makerToken,
  takerToken,
  page,
  limit,
}: {
  funders: LimitOrderFunders;
  searchTerm?: string;
  chainId?: number;
  makerToken?: ChainAddress;
  takerToken?: ChainAddress;
  page: number;
  limit: number;
}) {
  const makers = [funders.ethereum, funders.solana].filter(
    (funder): funder is ChainAddress => funder !== undefined,
  );

  return useLimitOrders({
    searchTerm,
    chainId,
    makers,
    makerToken,
    takerToken,
    page,
    limit,
  });
}

export type TradeHistoryFilters = {
  advancedOrderStatus?: AdvancedClosedOrderStatus;
  chainIds?: string[];
  /** Unset fetches only the indexed swap lanes; limit orders need an explicit request. */
  orderTypes?: string | string[];
  searchTerm?: string;
  startTimestamp?: string;
  endTimestamp?: string;
  tokenAddresses?: ChainAddress[];
  /** Splits limit orders into one entry per finalized fill. */
  expandLimitFills?: boolean;
  /** Drops unsettled indexed rows server-side, before pagination. */
  successfulOnly?: boolean;
};

const getTradeHistoryQueryArgs = ({
  funders,
  filters,
}: {
  funders: LimitOrderFunders;
  filters: TradeHistoryFilters;
}) => {
  const addresses = [
    funders.ethereum?.toLowerCase() as EthereumAddress | undefined,
    funders.solana,
  ].filter((funder): funder is ChainAddress => funder !== undefined);

  return addresses.length > 0
    ? {
        addresses: addresses.join(','),
        ...filters,
      }
    : undefined;
};

export function useWalletTradeHistory({
  funders,
  filters,
  offset,
  limit,
  enabled = true,
  holdPreviousData = false,
}: {
  funders: LimitOrderFunders;
  filters: TradeHistoryFilters;
  offset: number;
  limit: number;
  enabled?: boolean;
  holdPreviousData?: boolean;
}) {
  const baseQueryArgs = getTradeHistoryQueryArgs({ funders, filters });
  const queryArgs = baseQueryArgs ? { ...baseQueryArgs, offset, limit } : undefined;
  const query = queryArgs ? `?${qs.stringify(queryArgs)}` : undefined;

  return useQuery<TradeHistoryResponse, Error>({
    queryKey: ['trade-history', queryArgs],
    queryFn: () => fetcher(routes.api.TRADE_HISTORY + query!),
    enabled: !!query && enabled,
    placeholderData: holdPreviousData ? keepPreviousData : undefined,
  });
}

const MAX_TRADE_HISTORY_PAGES = 20;

/** Drains every trade-history page for the filters and returns one flat list. */
export function useWalletTradeHistoryPages({
  funders,
  filters,
  limit,
  enabled = true,
}: {
  funders: LimitOrderFunders;
  filters: TradeHistoryFilters;
  limit: number;
  enabled?: boolean;
}) {
  const queryArgs = getTradeHistoryQueryArgs({ funders, filters });

  return useQuery<MatchaTradeHistoryEntry[], Error>({
    queryKey: ['trade-history', 'all-pages', queryArgs, limit],
    queryFn: async () => {
      const trades: MatchaTradeHistoryEntry[] = [];
      // Offsets shift when a trade lands mid-drain, so boundary rows can repeat across pages.
      const seen = new Set<string>();

      for (let page = 0; page < MAX_TRADE_HISTORY_PAGES; page += 1) {
        const query = qs.stringify({ ...queryArgs!, offset: page * limit, limit });
        const response: TradeHistoryResponse = await fetcher(
          `${routes.api.TRADE_HISTORY}?${query}`,
        );
        for (const trade of response.trades) {
          const identity = getTradeIdentity(trade);
          if (seen.has(identity)) continue;
          seen.add(identity);
          trades.push(trade);
        }
        if (response.trades.length < limit || seen.size >= response.count) break;
      }

      return trades;
    },
    enabled: !!queryArgs && enabled,
  });
}
